import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { env } from './lib/env';
import './index.css';

/** How long the boot splash stays up at minimum, and how long its fade lasts. */
const SPLASH_MIN_MS = 450;
const SPLASH_FADE_MS = 250;

/**
 * Hands the screen over from the inline splash in `index.html` to React.
 *
 * The minimum hold is the point: on a warm cache the app is ready in ~80ms, and
 * a splash that appears and disappears inside one blink reads as a rendering
 * glitch. Holding it briefly and cross-fading turns the same load into a
 * deliberate-looking transition — at zero cost, because the app is already
 * mounted and painted behind it.
 */
function dismissSplash(startedAt: number) {
  const splash = document.querySelector('#boot');
  if (!(splash instanceof HTMLElement)) return;

  const wait = Math.max(0, SPLASH_MIN_MS - (Date.now() - startedAt));
  setTimeout(() => {
    splash.classList.add('is-done');
    setTimeout(() => splash.remove(), SPLASH_FADE_MS);
  }, wait);
}

/**
 * The mock API must be intercepting before the first request goes out, so the
 * app is mounted only after the worker is ready. Turning mocks off
 * (VITE_USE_MOCKS=false) skips this entirely and the same code talks to
 * Spring Boot.
 */
async function bootstrap() {
  const startedAt = Date.now();

  if (env.useMocks) {
    const { startMockApi } = await import('@/mocks/browser');
    await startMockApi();
  }

  const container = document.querySelector('#root');
  if (!container) throw new Error('Root element #root was not found in index.html');

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  // Two frames: one for React to commit, one for the browser to paint it. Only
  // then is there something behind the splash worth revealing.
  requestAnimationFrame(() => requestAnimationFrame(() => dismissSplash(startedAt)));
}

void bootstrap();
